import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/auth";
import { AutoRefreshProvider } from "@/lib/autoRefresh";
import Layout from "@/components/Layout";
import InactivityGuard from "@/components/InactivityGuard";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Store from "@/pages/Store";
import Dashboard from "@/pages/Dashboard";
import Catalog from "@/pages/Catalog";
import Orders from "@/pages/Orders";
import Subscriptions from "@/pages/Subscriptions";
import Invoices from "@/pages/Invoices";
import Payments from "@/pages/Payments";
import Tickets from "@/pages/Tickets";
import Users from "@/pages/Users";
import Perfil from "@/pages/Perfil";
import Audit from "@/pages/Audit";
import CogniEtica from "@/pages/CogniEtica";
import Privaxia from "@/pages/Privaxia";
import ConnectorsExecutivePanel from "@/pages/ConnectorsExecutivePanel";
import WebhookEvents from "@/pages/WebhookEvents";
import DynamicConnectorPage from "@/pages/DynamicConnectorPage";
import Maintenance from "@/pages/Maintenance";
import DisasterRecovery from "@/pages/DisasterRecovery";
import KBAttachments from "@/pages/KBAttachments";
import SearchPage from "@/pages/Search";
import Accounts from "@/pages/crm/Accounts";
import AccountDetail from "@/pages/crm/AccountDetail";
import Contacts from "@/pages/crm/Contacts";
import ConsentHistory from "@/pages/crm/ConsentHistory";
import Duplicates from "@/pages/crm/Duplicates";
import Opportunities from "@/pages/crm/Opportunities";
import MrrDashboard from "@/pages/crm/MrrDashboard";
import Team from "@/pages/Team";
import Tasks from "@/pages/Tasks";
import Approvals from "@/pages/Approvals";
import Workflows from "@/pages/Workflows";
import Channels from "@/pages/Channels";
import Forms from "@/pages/Forms";
import Scheduling from "@/pages/Scheduling";
import Settings from "@/pages/Settings";
import Security from "@/pages/Security";
import Proposals from "@/pages/Proposals";
import PublicProposal from "@/pages/PublicProposal";
import Sign from "@/pages/Sign";
import Contracts from "@/pages/Contracts";
import { Commissions, Discounts } from "@/pages/Misc";
import Portal from "@/pages/Portal";
import SetPassword from "@/pages/SetPassword";
import PortalLogin from "@/pages/PortalLogin";
import PublicForm from "@/pages/PublicForm";
import Activities from "@/pages/Activities";
import ContractTemplates from "@/pages/ContractTemplates";
import LeadQualification from "@/pages/LeadQualification";
import Platforms from "@/pages/Platforms";
import Partners from "@/pages/Partners";
import ChatInbox from "@/pages/ChatInbox";
import Handoffs from "@/pages/Handoffs";
import LeadKanban from "@/pages/LeadKanban";
import EmailTemplates from "@/pages/EmailTemplates";
import DealsKanban from "@/pages/DealsKanban";
import PartnerPortal from "@/pages/PartnerPortal";
import Marketing from "@/pages/Marketing";
import CampaignEditor from "@/pages/CampaignEditor";
import PortalPreview from "@/pages/PortalPreview";

function isPureClient(user) {
  const roles = (user && user.roles) || [];
  return roles.length > 0 && roles.every((r) => r === "cliente");
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-muted2">A carregar...</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if ((user.roles || []).includes("parceiro")) return <Navigate to="/partner" replace />;
  if (isPureClient(user)) return <Navigate to="/portal" replace />;
  return <Layout>{children}</Layout>;
}

function ProtectedPartner({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-muted2">A carregar...</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (!((user.roles || []).includes("parceiro"))) return <Navigate to="/app" replace />;
  return children;
}

function ProtectedPortal({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-muted2">A carregar...</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (!((user.roles || []).includes("cliente"))) return <Navigate to="/app" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <AutoRefreshProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <InactivityGuard />
          <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/portal" element={<ProtectedPortal><Portal /></ProtectedPortal>} />
            <Route path="/partner" element={<ProtectedPartner><PartnerPortal /></ProtectedPartner>} />
            <Route path="/definir-password/:token" element={<SetPassword />} />
            <Route path="/portal-login" element={<PortalLogin />} />
            <Route path="/f/:id" element={<PublicForm />} />
            <Route path="/loja" element={<Store />} />
            <Route path="/store" element={<Store />} />
            <Route path="/app/loja" element={<Protected><Store /></Protected>} />
            <Route path="/app/pesquisa" element={<Protected><SearchPage /></Protected>} />
            <Route path="/app/crm/contas" element={<Protected><Accounts /></Protected>} />
            <Route path="/app/crm/contas/:id" element={<Protected><AccountDetail /></Protected>} />
            <Route path="/app/crm/contactos" element={<Protected><Contacts /></Protected>} />
            <Route path="/app/crm/consentimentos" element={<Protected><ConsentHistory /></Protected>} />
            <Route path="/app/crm/duplicados" element={<Protected><Duplicates /></Protected>} />
            <Route path="/app/crm/oportunidades" element={<Protected><Opportunities /></Protected>} />
            <Route path="/app/crm/oportunidades/kanban" element={<Protected><DealsKanban /></Protected>} />
            <Route path="/app/crm/mrr-dashboard" element={<Protected><MrrDashboard /></Protected>} />
            <Route path="/app/crm/atividades" element={<Protected><Activities /></Protected>} />
            <Route path="/app/crm/qualificacao" element={<Protected><LeadQualification /></Protected>} />
            <Route path="/app/crm/qualificacao/kanban" element={<Protected><LeadKanban /></Protected>} />
            <Route path="/app/equipa" element={<Protected><Team /></Protected>} />
            <Route path="/app/equipa-vendas" element={<Protected><Team /></Protected>} />
            <Route path="/app/inbox-canais" element={<Protected><ChatInbox /></Protected>} />
            <Route path="/app/handoffs" element={<Protected><Handoffs /></Protected>} />
            <Route path="/app/agenda" element={<Protected><Scheduling /></Protected>} />
            <Route path="/app/workflows" element={<Protected><Workflows /></Protected>} />
            <Route path="/app/tarefas" element={<Protected><Tasks /></Protected>} />
            <Route path="/app/aprovacoes" element={<Protected><Approvals /></Protected>} />
            <Route path="/app/automacao" element={<Protected><Workflows /></Protected>} />
            <Route path="/app/canais" element={<Protected><Channels /></Protected>} />
            <Route path="/app/formularios" element={<Protected><Forms /></Protected>} />
            <Route path="/app/agendamentos" element={<Protected><Scheduling /></Protected>} />
            <Route path="/assinar/:token" element={<Sign />} />
            <Route path="/proposta/:token" element={<PublicProposal />} />
            <Route path="/app" element={<Protected><Dashboard /></Protected>} />
            <Route path="/app/propostas" element={<Protected><Proposals /></Protected>} />
            <Route path="/app/contratos" element={<Protected><Contracts /></Protected>} />
            <Route path="/app/contratos/templates" element={<Protected><ContractTemplates /></Protected>} />
            <Route path="/app/email-templates" element={<Protected><EmailTemplates /></Protected>} />
            <Route path="/app/marketing" element={<Protected><Marketing /></Protected>} />
            <Route path="/app/marketing/editor/:id" element={<Protected><CampaignEditor /></Protected>} />
            <Route path="/app/marketing/editor" element={<Protected><CampaignEditor /></Protected>} />
            <Route path="/app/catalogo" element={<Protected><Catalog /></Protected>} />
            <Route path="/app/precos" element={<Protected><Discounts /></Protected>} />
            <Route path="/app/encomendas" element={<Protected><Orders /></Protected>} />
            <Route path="/app/subscricoes" element={<Protected><Subscriptions /></Protected>} />
            <Route path="/app/faturacao" element={<Protected><Invoices /></Protected>} />
            <Route path="/app/pagamentos" element={<Protected><Payments /></Protected>} />
            <Route path="/app/suporte" element={<Protected><Tickets /></Protected>} />
            <Route path="/app/anexos-kb" element={<Protected><KBAttachments /></Protected>} />
            <Route path="/app/comissoes" element={<Protected><Commissions /></Protected>} />
            <Route path="/app/parceiros" element={<Protected><Partners /></Protected>} />
            <Route path="/app/cognietica" element={<Protected><CogniEtica /></Protected>} />
            <Route path="/app/privaxia" element={<Protected><Privaxia /></Protected>} />
            <Route path="/app/painel-plataformas" element={<Protected><ConnectorsExecutivePanel /></Protected>} />
            <Route path="/app/webhook-events" element={<Protected><WebhookEvents /></Protected>} />
            <Route path="/app/connectors/:slug" element={<Protected><DynamicConnectorPage /></Protected>} />
            <Route path="/app/plataformas" element={<Protected><Platforms /></Protected>} />
            <Route path="/app/utilizadores" element={<Protected><Users /></Protected>} />
            <Route path="/app/perfil" element={<Protected><Perfil /></Protected>} />
            <Route path="/app/auditoria" element={<Protected><Audit /></Protected>} />
            <Route path="/app/manutencao" element={<Protected><Maintenance /></Protected>} />
            <Route path="/app/dr" element={<Protected><DisasterRecovery /></Protected>} />
            <Route path="/app/definicoes" element={<Protected><Settings /></Protected>} />
            <Route path="/app/seguranca" element={<Protected><Security /></Protected>} />
            <Route path="/app/portal-cliente" element={<Protected><PortalPreview /></Protected>} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </BrowserRouter>
        </AutoRefreshProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
